import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import PrivateRoute from "./PrivateRoute";

import Footer from "./components/Footer";
import ForgotPassword from "./components/ForgotPassword";
import Header from "./components/Header";
import Homepage from "./components/Homepage";
import ImpersonationBanner from "./components/ImpersonationBanner";
import Login from "./components/Login";
import MobileTabBar from "./components/MobileTabBar";
import ResetPassword from "./components/ResetPassword";
import Signup from "./components/Signup";

// Import company components
import CompanyCreate from "./components/CompanyCreate";
import CompanyDetail from "./components/CompanyDetail";
import CompanyEdit from "./components/CompanyEdit";
import CompanyList from "./components/CompanyList";

// Import patient components
import PatientCreate from "./components/PatientCreate";
import PatientDetail from "./components/PatientDetail";
import PatientList from "./components/PatientList";
// import PatientEdit from './components/PatientEdit';

// Import lead components
import LeadCreate from "./components/LeadCreate";
import LeadDetail from "./components/LeadDetail";
import LeadEdit from "./components/LeadEdit";
import LeadList from "./components/LeadList";

// Import opportunity components
import OpportunityList from "./components/OpportunityList";

// Import account components
import AccountList from "./components/AccountList";

// Import notes components
import MyNotes from "./components/MyNotes";
import MyTasks from "./components/MyTasks";
import NoteDetail from "./components/NoteDetail";
import NotesList from "./components/NotesList";

// Import tasks components
import TaskDetail from "./components/TaskDetail";
import TasksList from "./components/TasksList";

import ServiceCreate from "./components/ServiceCreate";
import ServiceEdit from "./components/ServiceEdit";
import ServiceList from "./components/ServiceList";

import AppointmentCreate from "./components/AppointmentCreate";
import AppointmentEdit from "./components/AppointmentEdit";
import AppointmentList from "./components/AppointmentList";
import ChangePassword from "./components/ChangePassword";

// Import info component
import Info from "./components/Info";

import { useLocation } from 'react-router-dom';

import axios from 'axios';
import config from './config';

import { useUser } from './components/UserContext';
import { getInfo } from './services/api';
import { identifyUser, optInAnalytics, optOutAnalytics, resetAnalytics, track } from './services/analytics';
import { usePageviewTracking } from './hooks/usePageviewTracking';
import { useIsMobile } from './hooks/useIsMobile';

import LeadMessage from "./components/LeadMessage";
import MessageTemplates from "./components/MessageTemplates";
import ServicesManagement from "./components/ServicesManagement";
import Spotlight from "./components/Spotlight";
import { LiveActivityProvider } from "./hooks/useLiveActivity";
import { useSpotlight } from "./context/SpotlightContext";

function AppContent() {
  // "checking" until the startup check below settles. PrivateRoute waits for it instead of
  // a timer, so a slow check (for example a token refresh) never sends a signed-in user
  // to /login.
  const [authStatus, setAuthStatus] = useState('checking');
  const isLoggedIn = authStatus === 'signed_in';
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { authToken, setAuthToken, userInfo, setUserInfo, locationInfo, setLocationInfo } = useUser();

  usePageviewTracking();
  const isMobile = useIsMobile();
  const showTabBar = isMobile && isLoggedIn;

  // Spotlight seen state lives on the server, so it follows the person, not the browser.
  const { loadSeenAnnouncements, clearSeenAnnouncements } = useSpotlight();
  useEffect(() => {
    if (isLoggedIn) {
      loadSeenAnnouncements();
    } else {
      clearSeenAnnouncements();
    }
  }, [isLoggedIn, loadSeenAnnouncements, clearSeenAnnouncements]);

  const api = axios.create({
    baseURL: config.BASE_API_ENDPOINT,
    withCredentials: true // Important for cookie handling
  });

  // Initialize state from localStorage and validate token
  useEffect(() => {
    const checkImpersonationAndAuth = async () => {
      // First check if there's an active impersonation session
      try {
        // Use backend URL based on environment
        const isDevelopment = window.location.hostname === 'localhost';
        const baseUrl = isDevelopment ? 'http://localhost:8000' : '';
        
        const impersonationResponse = await fetch(`${baseUrl}/api/impersonation/status/`, {
          credentials: 'include'
        });
        
        if (impersonationResponse.ok) {
          const impersonationData = await impersonationResponse.json();
          
          if (impersonationData.impersonating) {
            // Never track impersonated sessions — admin activity would pollute
            // the practice's own analytics. Opt out before anything else here.
            optOutAnalytics();

            // We're in an impersonation session, get fresh tokens
            console.log('Impersonation detected, fetching user info...');
            
            try {
              // During impersonation, use session-based API call instead of JWT
              const userInfoResponse = await fetch(`${baseUrl}/api/info/`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json',
                }
              });
              
              if (userInfoResponse.ok) {
                const userData = await userInfoResponse.json();
                console.log('Got impersonated user data:', userData);
                
                // Set up the session with impersonated user data
                setAuthToken('impersonation_session'); // Placeholder token
                setUserInfo(userData);
                
                // Try to get location info
                if (userData.staff && userData.staff.locations.length > 0) {
                  setLocationInfo(userData.staff.locations[0]);
                  localStorage.setItem('locationInfo', JSON.stringify(userData.staff.locations[0]));
                }
                
                localStorage.setItem('userInfo', JSON.stringify(userData));
                localStorage.setItem('authToken', 'impersonation_session');
                setAuthStatus('signed_in');
                return; // Skip normal auth check
              } else {
                console.error('Failed to get user info during impersonation:', userInfoResponse.status);
              }
              
            } catch (error) {
              console.error('Failed to get impersonated user info:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error checking impersonation status:', error);
      }
      
      // Normal authentication check
      const token = localStorage.getItem("authToken");
      const accessToken = localStorage.getItem("access_token");
      
      if (token && accessToken) {
        try {
          const storedUserInfo = localStorage.getItem("userInfo");
          const storedLocationInfo = localStorage.getItem("locationInfo");
          
          // Validate token by making a quick API call
          const validateAuth = async () => {
            try {
              await getInfo(); // This will trigger 401 if token is invalid
              
              // Only set logged in state if validation succeeds
              setAuthToken(token);
              if (storedUserInfo) {
                setUserInfo(JSON.parse(storedUserInfo));
              }
              if (storedLocationInfo) {
                setLocationInfo(JSON.parse(storedLocationInfo));
              }
              // A page reload must not go anonymous
              if (storedUserInfo && storedLocationInfo) {
                identifyUser(JSON.parse(storedUserInfo), JSON.parse(storedLocationInfo));
              }
              setAuthStatus('signed_in');
            } catch (error) {
              console.error('Token validation failed:', error);
              // Clear all auth data if token is invalid
              localStorage.removeItem('authToken');
              localStorage.removeItem('userInfo');
                localStorage.removeItem('locationInfo');
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              setAuthStatus('signed_out');
              setAuthToken('');
              setUserInfo(null);
              setLocationInfo(null);
            }
          };
          
          validateAuth();
        } catch (error) {
          console.error('Error parsing localStorage:', error);
          // Clear localStorage on error to prevent persistent issues
          localStorage.removeItem('authToken');
          localStorage.removeItem('userInfo');
          localStorage.removeItem('locationInfo');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          setAuthStatus('signed_out');
          setAuthToken('');
          setUserInfo(null);
          setLocationInfo(null);
        }
      } else {
        // Clear localStorage if no tokens
        localStorage.removeItem('authToken');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('locationInfo');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setAuthStatus('signed_out');
        setAuthToken('');
        setUserInfo(null);
        setLocationInfo(null);
      }
    };
    
    checkImpersonationAndAuth();
  }, [setAuthToken, setUserInfo, setLocationInfo]);

  // Listen for auth:logout event from API interceptor
  useEffect(() => {
    const handleAuthLogout = () => {
      track('user_logged_out');
      resetAnalytics();
      setAuthStatus('signed_out');
      setAuthToken('');
      setUserInfo(null);
      setLocationInfo(null);
    };

    window.addEventListener('auth:logout', handleAuthLogout);
    
    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout);
    };
  }, [setAuthToken, setUserInfo, setLocationInfo]);


  // Fetch locationInfo if missing
  useEffect(() => {
    if (['/login', '/signup'].includes(location.pathname) || !isLoggedIn) return;
    if (authToken && !locationInfo?.company) {
      const fetchLocationInfo = async () => {
        try {
          console.log('Fetching location info in useEffect...');
          // Store access token for the API interceptor
          localStorage.setItem('access_token', authToken);
          const info = await getInfo();
          console.log('Location info from useEffect:', info);
          setLocationInfo(info);
          localStorage.setItem('locationInfo', JSON.stringify(info));
        } catch (error) {
          console.error('Error fetching location info in useEffect:', error);
          setLocationInfo(null);
          localStorage.removeItem('locationInfo');
        }
      };
      fetchLocationInfo();
    }
  }, [authToken, locationInfo, setLocationInfo, isLoggedIn, location.pathname]);

const handleLogout = async () => {
  // Front-desk machines are shared — reset before anything can fail, or the next
  // user's events attach to the previous user's person profile.
  track('user_logged_out');
  resetAnalytics();

  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('locationInfo');

    await api.post('/api/logout/');
    setAuthStatus('signed_out');
    setAuthToken("");
    setUserInfo(null);
    setLocationInfo(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('locationInfo');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    navigate("/login");
  } catch (error) {
    console.error('Logout error:', error);
    setAuthStatus('signed_out');
    setAuthToken('');
    setUserInfo(null);
    setLocationInfo(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('locationInfo');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    navigate("/login");
  }
};

  const handleLogin = useCallback( async (loginData) => {
    console.log('Login data received:', loginData); // Debug log

    // Clear all existing session data to prevent stale impersonation or user state from bleeding in
    localStorage.removeItem("authToken");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("locationInfo");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setLocationInfo(null);

    const newUserInfo = {
      isCompanyAdmin: loginData.is_company_admin,
      companyId: loginData.company,
      locationIds: loginData.locations,
      role: loginData.role,
    };

    // Set auth token first so API calls work
    setAuthToken(loginData.token);
    setUserInfo(newUserInfo);
    setAuthStatus('signed_in');
    localStorage.setItem("authToken", loginData.token);
    localStorage.setItem("access_token", loginData.access); // For API interceptor
    localStorage.setItem("userInfo", JSON.stringify(newUserInfo));

    // Try to fetch location info immediately after login
    try {
      console.log('Fetching location info after login...');
      const info = await getInfo();
      console.log('Location info from API:', info);
      setLocationInfo(info);
      localStorage.setItem('locationInfo', JSON.stringify(info));
      // Undo any opt-out left behind by a previous impersonation session
      optInAnalytics();
      identifyUser(newUserInfo, info);
      track('user_logged_in');
    } catch (error) {
      console.error('Error fetching location info after login:', error);
      // Fallback to login data if available
      const fallbackLocationInfo = {
        company: loginData.company_name || loginData.company,
      };
      console.log('Using fallback location info:', fallbackLocationInfo);
      setLocationInfo(fallbackLocationInfo);
      localStorage.setItem("locationInfo", JSON.stringify(fallbackLocationInfo));
    }

    navigate("/home");
  }, [navigate, setAuthToken, setUserInfo, setLocationInfo]);



  const handleSignup = (token) => {
    setAuthToken(token);
    setAuthStatus('signed_in');
    localStorage.setItem("authToken", token);
  };

  const onMenuSelect = (path) => {
    navigate(path);
  };

  return (
    <LiveActivityProvider enabled={isLoggedIn}>
      <div className="flex flex-col min-h-screen bg-background">
        <ImpersonationBanner />
        <Header
          isLoggedIn={isLoggedIn}
          locationInfo={locationInfo}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          handleLogout={handleLogout}
          onMenuSelect={onMenuSelect}
          activeRoute={location.pathname}
        />
        <Spotlight />
        <main
          className="flex-1 bg-background"
          style={showTabBar ? { paddingBottom: 'calc(4rem + env(safe-area-inset-bottom))' } : undefined}
        >
          <div className="w-full mx-auto px-5 sm:px-2 lg:px-5 py-0">
            <Routes>
              <Route
                path="/"
                element={
                  authStatus !== 'signed_out' ? (
                    <PrivateRoute authStatus={authStatus}>
                      <Homepage authToken={authToken} />
                    </PrivateRoute>
                  ) : (
                    <Login onLogin={handleLogin} />
                  )
                }
              />
              <Route
                path="/login"
                element={isLoggedIn ? <Navigate to="/home" replace /> : <Login onLogin={handleLogin} />}
              />
              <Route
                path="/signup"
                element={<Signup onSignup={handleSignup} />}
              />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route
                path="/home"
                element={
                  <PrivateRoute authStatus={authStatus}>
                    <Homepage authToken={authToken} />
                  </PrivateRoute>
                }
              />
  
              {/* Company Routes */}
              <Route
                path="/companies"
                element={
                  <PrivateRoute authStatus={authStatus}>
                    <CompanyList authToken={authToken} />
                  </PrivateRoute>
                }
              />
              <Route
                path="/companies/create"
                element={<CompanyCreate authToken={authToken} />}
              />
              <Route
                path="/companies/:id"
                element={<CompanyDetail authToken={authToken} />}
              />
              <Route
                path="/companies/:id/edit"
                element={<CompanyEdit authToken={authToken} />}
              />
  
              {/* Patient Routes */}
              <Route
                path="/patients"
                element={<PatientList authToken={authToken} />}
              />
              <Route
                path="/patients/create"
                element={<PatientCreate authToken={authToken} />}
              />
              <Route
                path="/patients/:id"
                element={<PatientDetail authToken={authToken} />}
              />
              {/* <Route path="/patients/:id/edit" element={<PatientEdit authToken={authToken} />} /> */}
  
              {/* Lead Routes */}
              <Route path="/leads" element={<LeadList authToken={authToken} />} />
              <Route
                path="/leads/create"
                element={<LeadCreate authToken={authToken} />}
              />
              <Route
                path="/leads/:id"
                element={<LeadDetail authToken={authToken} />}
              />
              <Route
                path="/leads/:leadId/edit"
                element={<LeadEdit authToken={authToken} />}
              />
  
              {/* Message Templates Route */}
              <Route
                path="/templates"
                element={
                  <PrivateRoute authStatus={authStatus}>
                    <MessageTemplates authToken={authToken} />
                  </PrivateRoute>
                }
              />
  
              {/* Services Management Route */}
              <Route
                path="/services"
                element={
                  <PrivateRoute authStatus={authStatus}>
                    <ServicesManagement authToken={authToken} />
                  </PrivateRoute>
                }
              />
  
              {/* Opportunity Routes */}
              <Route
                path="/opportunities"
                element={<OpportunityList authToken={authToken} />}
              />
  
              {/* Account Routes */}
              <Route
                path="/accounts"
                element={<AccountList authToken={authToken} />}
              />
  
              {/* note routes */}
              <Route
                path="/leads/:leadId/notes"
                element={<NotesList authToken={authToken} />}
              />
              <Route
                path="/leads/:leadId/notes/:noteId"
                element={<NoteDetail authToken={authToken} />}
              />
              <Route
                path="/my-notes"
                element={<MyNotes authToken={authToken} />}
              />
  
              {/* task routes */}
              <Route
                path="/patients/:patientId/tasks"
                element={<TasksList authToken={authToken} />}
              />
              <Route
                path="/patients/:patientId/tasks/:taskId"
                element={<TaskDetail authToken={authToken} />}
              />
  
              <Route
                path="/leads/:patientId/tasks"
                element={<TasksList authToken={authToken} />}
              />
              <Route
                path="/leads/:patientId/tasks/:taskId"
                element={<TaskDetail authToken={authToken} />}
              />
  
              <Route
                path="/my-tasks"
                element={<MyTasks authToken={authToken} />}
              />
              <Route
                path="/tasks/:taskId"
                element={<TaskDetail authToken={authToken} />}
              />
  
              {/* services routes */}
              <Route
                path="/companies/:companyId/services"
                element={<ServiceList authToken={authToken} />}
              />
              <Route
                path="/companies/:companyId/services/:serviceId/edit/"
                element={<ServiceEdit authToken={authToken} />}
              />
              <Route
                path="/companies/:companyId/services/new"
                element={<ServiceCreate authToken={authToken} />}
              />
  
              {/* Appointment routes */}
              <Route
                path="/patients/:patientId/appointments/"
                element={<AppointmentList authToken={authToken} />}
              />
              <Route
                path="/patients/:patientId/appointments/new"
                element={<AppointmentCreate authToken={authToken} />}
              />
              <Route
                path="/patients/:patientId/appointments/:appointmentId/edit"
                element={<AppointmentEdit authToken={authToken} />}
              />
  
              {/* info routes */}
              <Route path="/info" element={<Info authToken={authToken} />} />
  
              <Route
                path="/change-password"
                element={<ChangePassword authToken={authToken} />}
              />
  
              <Route
                path="/leads/:leadId/messages/send/"
                element={
                  <PrivateRoute authStatus={authStatus}>
                    <LeadMessageWrapper authToken={authToken} />
                  </PrivateRoute>
                }
              />
              <Route
                path="/leads/:leadId/messages"
                element={
                  <PrivateRoute authStatus={authStatus}>
                    <LeadMessageWrapper authToken={authToken} />
                  </PrivateRoute>
                }
              />
            </Routes>
          </div>
        </main>
        {showTabBar && <MobileTabBar activeRoute={location.pathname} />}
        <Footer />
      </div>
    </LiveActivityProvider>
  );
}

function LeadMessageWrapper({ authToken }) {
  const { leadId } = useParams();
  return <LeadMessage leadId={parseInt(leadId)} authToken={authToken} />;
}

export default AppContent;
